import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ApplyGroupingOverridesRequest,
  DesignModelRequestDto,
} from 'src/api/FiretrolApiClient';
import { designModelApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { invalidateFor } from 'src/api/queries/invalidationMap';

export function useCreateDesignModel(jobId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: DesignModelRequestDto) =>
      designModelApiClient.create(request),
    meta: {
      successMessage: 'Design model created',
      suppressErrorToast: true,
    },
    onSuccess: (model) => {
      qc.setQueryData(queryKeys.designModels.detail(model.id), model);
    },
    onSettled: () => invalidateFor('designModel.create', qc, { jobId }),
  });
}

export function useUpdateDesignModel(jobId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id: string;
      request: DesignModelRequestDto;
    }) => designModelApiClient.update(id, request),
    meta: {
      successMessage: 'Design model updated',
      suppressErrorToast: true,
    },
    onSuccess: (model) => {
      qc.setQueryData(queryKeys.designModels.detail(model.id), model);
    },
    onSettled: (_data, _err, { id }) =>
      invalidateFor('designModel.update', qc, { jobId, id }),
  });
}

export function useDeleteDesignModel(jobId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => designModelApiClient.delete(id),
    meta: { successMessage: 'Design model deleted' },
    onSettled: () => invalidateFor('designModel.delete', qc, { jobId }),
  });
}

export function useReprocessDesignModel(jobId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => designModelApiClient.reprocess(id),
    meta: { successMessage: 'Reprocessing started', suppressErrorToast: true },
    onSuccess: (model) => {
      qc.setQueryData(queryKeys.designModels.detail(model.id), model);
    },
    onSettled: (_data, _err, id) =>
      invalidateFor('designModel.reprocess', qc, { jobId, id }),
  });
}

export function useApplyGroupingOverrides(jobId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id: string;
      request: ApplyGroupingOverridesRequest;
    }) => designModelApiClient.applyGroupingOverrides(id, request),
    meta: { successMessage: 'Grouping updated' },
    onSettled: (_data, _err, { id }) =>
      invalidateFor('designModel.grouping', qc, { jobId, id }),
  });
}

export function useResetGroupingOverrides(jobId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => designModelApiClient.resetGroupingOverrides(id),
    meta: { successMessage: 'Grouping reset to parser output' },
    onSettled: (_data, _err, id) =>
      invalidateFor('designModel.grouping', qc, { jobId, id }),
  });
}
